import { toast } from "sonner";

const PIN_STORAGE_KEY = "cargoload_admin_pin";
// sessionStorage: PIN clears when the tab closes → smaller attack surface than
// localStorage without needing httpOnly cookies (the app has no user auth).
const storage = typeof window !== "undefined" ? window.sessionStorage : null;

export function getStoredPin() {
  if (!storage) return "";
  try {
    return storage.getItem(PIN_STORAGE_KEY) || "";
  } catch (err) {
    console.warn("adminPin: sessionStorage read failed", err);
    return "";
  }
}

export function setStoredPin(pin) {
  if (!storage) return;
  try {
    if (pin) {
      storage.setItem(PIN_STORAGE_KEY, pin);
    } else {
      storage.removeItem(PIN_STORAGE_KEY);
    }
  } catch (err) {
    console.warn("adminPin: sessionStorage write failed", err);
  }
}

/** Prompt the user for the PIN if none is stored, or if `force` is true.
 *  Returns the entered PIN (as-is) or null if the user cancelled. */
export function promptForPin({ force = false } = {}) {
  const cached = getStoredPin();
  if (cached && !force) return cached;
  // eslint-disable-next-line no-alert
  const entered = window.prompt(
    force
      ? "The saved admin PIN was rejected. Enter the current admin PIN to continue:"
      : "Admin actions (status change / delete / notes) are PIN-protected. Enter the admin PIN:"
  );
  if (entered === null) return null;
  const trimmed = entered.trim();
  if (!trimmed) return null;
  setStoredPin(trimmed);
  return trimmed;
}

/** Execute an async admin action with PIN handling. If the backend returns 401,
 *  clear the stored PIN, prompt the user for a fresh one, and retry once. */
export async function withAdminPin(action) {
  let pin = promptForPin();
  if (!pin) {
    toast.info("Cancelled — admin PIN not entered");
    return null;
  }
  try {
    return await action(pin);
  } catch (err) {
    if (err?.response?.status === 401) {
      setStoredPin("");
      pin = promptForPin({ force: true });
      if (!pin) {
        toast.info("Cancelled");
        return null;
      }
      return await action(pin);
    }
    throw err;
  }
}

export function clearStoredPin() {
  setStoredPin("");
}
